import useUserData, { useUserArticlesQuery } from 'hooks/useUserData';
import { useMemo, useReducer, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useReportModalStore from 'store/useReportModalStore';
import useShareModalStore from 'store/useShareModalStore';
import {
  catchErrorMessage,
  splitFilesByType,
  getExtraFileNameAttachedByService,
} from 'utility/helpers';
import {
  ArticleSourceType,
  ArticleType,
  Endorsement,
  useArticlesAndPlaylistsPaginationQuery,
} from './useArticlesQuery';
import { MenuItemType } from 'components/drawer/RenderMenu';
import CloseCircleLineIcon from 'remixicon-react/CloseCircleLineIcon';
import EditLineIcon from 'remixicon-react/EditLineIcon';
import FileListLineIcon from 'remixicon-react/FileListLineIcon';
import FlagLineIcon from 'remixicon-react/FlagLineIcon';
import PlayListAddLineIcon from 'remixicon-react/PlayListAddLineIcon';
import FileCopyLineIcon from 'remixicon-react/FileCopyLineIcon';
import ShareLineIcon from 'remixicon-react/ShareLineIcon';
import StarSmileLineIcon from 'remixicon-react/StarSmileLineIcon';
import UserAddLineIcon from 'remixicon-react/UserAddLineIcon';
import UserShared2LineIcon from 'remixicon-react/UserShared2LineIcon';
import KnowledgeAPI, { FeedFilter } from 'api/KnowledgeAPI';
import { updateSearchQueriesCache } from 'hooks/useSearchQuery';
import {
  updateArticleDetailsCache,
  usePaginatedRelatedArticlesQuery,
} from 'pages/article/hooks/useArticleDetailsQuery';
import { updateDraftsCache } from 'pages/drafts/hooks/useDraftsQuery';
import {
  updateProjectDetailsCache,
  updateProjectRecommendationsQueryCache,
} from 'pages/project/hooks/useProjectDetailsQuery';
import {
  useAssignedFormsQuery,
  useFormDetailsQuery,
} from '@buildwise/pages/forms/queries/useFormsQuery';
import { toast } from 'react-toastify';
import { ProjectRecommendationsType, ProjectType } from 'pages/projects/hooks/useProjectsQuery';
import { DRAFTS, HELP_CENTER, HOME } from 'constants/routes';
import { ReactComponent as BookmarkIcon } from 'assets/icons/bookmark.svg';
import { ArticleVariantType } from '../components/ArticleBox';
import DeleteBinLineIcon from 'remixicon-react/DeleteBinLineIcon';
import ArrowUpLineIcon from 'remixicon-react/ArrowUpLineIcon';
import { updateStakeholderArticlesCache } from 'pages/stakeholder/hooks/useStakeholderDetailsQuery';
import useBookmarksQuery from 'pages/bookmarks/hooks/useBookmarksQuery';
import ShareForwardLineIcon from 'remixicon-react/ShareForwardLineIcon';
import { useTheme } from '@mui/material/styles';
import DraftLineIcon from 'remixicon-react/DraftLineIcon';
import { FORMS_CONFIG_CATEGORY_TYPES } from 'config/config';
import QuestionLineIcon from 'remixicon-react/QuestionLineIcon';
import ChatQuoteLineIcon from 'remixicon-react/ChatQuoteLineIcon';
import { useBuildWiseModules } from '@buildwise/libs/helpers';

export type UseArticleReducerState = {
  loading: boolean;
  openInsightsModal: boolean;
  openInteractionsModal: boolean;
  openAddPostsToPlaylistModal: boolean;
  openPlaylistModal: boolean;
  openEndorseConfirmationModal: boolean;
  openDeleteModal: boolean;
  openEndorseInteractionsModal: boolean;
  openPostConfirmationModal: boolean;
  openTransferDraftModal: boolean;
  openInviteModal: boolean;
  openRejectModal: boolean;
  openEditPost: boolean;
  openCopyPost: boolean;
  openCollaborators: boolean;
  openNotRelevantArticleModal: boolean;
  openPostRevisionModal: boolean;
  openForwardPostModal: boolean;
  openSelectWorkspacesModal: boolean;
  openMovePostConfirmationModal: boolean;
  openMoveToDraftsConfirmationModal: boolean;
  lightbox: {
    open: boolean;
    fileName: string;
  };
  convertPostToQuestion: boolean;
};

export type UseArticleReducerActions =
  | {
      type:
        | 'TOGGLE_LOADING'
        | 'TOGGLE_INTERACTIONS_MODAL'
        | 'TOGGLE_INSIGHTS_MODAL'
        | 'TOGGLE_ADD_POSTS_TO_PLAYLIST_MODAL'
        | 'TOGGLE_PLAYLIST_MODAL'
        | 'TOGGLE_ENDORSE_CONFIRMATION_MODAL'
        | 'TOGGLE_DELETE_POST_MODAL'
        | 'TOGGLE_ENDORSE_INTERACTIONS_MODAL'
        | 'TOGGLE_POST_CONFIRMATION_MODAL'
        | 'TOGGLE_TRANSFER_DRAFT_MODAL'
        | 'TOGGLE_INVITE_MODAL'
        | 'TOGGLE_REJECT_MODAL'
        | 'TOGGLE_EDIT_MODAL'
        | 'TOGGLE_COPY_POST'
        | 'TOGGLE_COLLABORATORS_MODAL'
        | 'TOGGLE_NOT_RELEVANT_ARTICLE_MODAL'
        | 'TOGGLE_POST_REVISION_MODAL'
        | 'TOGGLE_FORWARD_POST_MODAL'
        | 'TOGGLE_SELECT_WORKSPACES_MODAL'
        | 'TOGGLE_MOVE_POST_MODAL'
        | 'TOGGLE_MOVE_TO_DRAFTS_MODAL';
      payload: boolean;
      key:
        | 'loading'
        | 'openInsightsModal'
        | 'openInteractionsModal'
        | 'openAddPostsToPlaylistModal'
        | 'openPlaylistModal'
        | 'openEndorseConfirmationModal'
        | 'openDeleteModal'
        | 'openEndorseInteractionsModal'
        | 'openPostConfirmationModal'
        | 'openTransferDraftModal'
        | 'openInviteModal'
        | 'openRejectModal'
        | 'openEditPost'
        | 'openCopyPost'
        | 'openCollaborators'
        | 'openNotRelevantArticleModal'
        | 'openPostRevisionModal'
        | 'openForwardPostModal'
        | 'openSelectWorkspacesModal'
        | 'openMovePostConfirmationModal'
        | 'openMoveToDraftsConfirmationModal';
    }
  | {
      type: 'TOGGLE_LIGHTBOX';
      payload: {
        open: boolean;
        fileName: string;
      };
    }
  | {
      type: 'TOGGLE_CONVERT_POST_TO_QUESTION';
      payload: boolean;
    };

function useArticleReducer(state: UseArticleReducerState, action: UseArticleReducerActions) {
  const { type, payload } = action;
  switch (type) {
    case 'TOGGLE_LOADING':
    case 'TOGGLE_INTERACTIONS_MODAL':
    case 'TOGGLE_INSIGHTS_MODAL':
    case 'TOGGLE_ADD_POSTS_TO_PLAYLIST_MODAL':
    case 'TOGGLE_PLAYLIST_MODAL':
    case 'TOGGLE_ENDORSE_CONFIRMATION_MODAL':
    case 'TOGGLE_DELETE_POST_MODAL':
    case 'TOGGLE_ENDORSE_INTERACTIONS_MODAL':
    case 'TOGGLE_POST_CONFIRMATION_MODAL':
    case 'TOGGLE_TRANSFER_DRAFT_MODAL':
    case 'TOGGLE_INVITE_MODAL':
    case 'TOGGLE_REJECT_MODAL':
    case 'TOGGLE_EDIT_MODAL':
    case 'TOGGLE_COPY_POST':
    case 'TOGGLE_COLLABORATORS_MODAL':
    case 'TOGGLE_NOT_RELEVANT_ARTICLE_MODAL':
    case 'TOGGLE_POST_REVISION_MODAL':
    case 'TOGGLE_FORWARD_POST_MODAL':
    case 'TOGGLE_SELECT_WORKSPACES_MODAL':
    case 'TOGGLE_MOVE_POST_MODAL':
    case 'TOGGLE_MOVE_TO_DRAFTS_MODAL':
      return { ...state, [action.key]: payload };

    case 'TOGGLE_CONVERT_POST_TO_QUESTION':
      return { ...state, convertPostToQuestion: payload };

    case 'TOGGLE_LIGHTBOX':
      return {
        ...state,
        lightbox: payload,
      };

    default:
      return state;
  }
}

type UseArticleArgsType = {
  article: ArticleType;
  articleIndex?: number;
  project?: ProjectType | ProjectRecommendationsType;
  selectedFeedFilters?: FeedFilter[];
  stakeholderId?: string;
  variant: ArticleVariantType;
  bookmarkUpdateCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  deleteArticleCallback?: (options: { article: ArticleType }) => void;
  endorseArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
  likeUpdateCallback?: (options: { oldArticle: ArticleType; updatedArticle: ArticleType }) => void;
  onEditArticleCallback?: () => void;
  resetFeed?: (options?: {
    resetQueries?: boolean | undefined;
    smoothScrollBehavior?: boolean | undefined;
  }) => void;
  updateArticleCallback?: (options: {
    oldArticle: ArticleType;
    updatedArticle: ArticleType;
  }) => void;
};

function useArticle({
  article,
  articleIndex,
  project,
  selectedFeedFilters,
  stakeholderId,
  variant,
  bookmarkUpdateCallback,
  deleteArticleCallback,
  endorseArticleCallback,
  likeUpdateCallback,
  onEditArticleCallback,
  resetFeed,
  updateArticleCallback,
}: UseArticleArgsType) {
  const theme = useTheme();
  const { pathname, state } = useLocation();
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const { articleId = '' } = useParams();
  const { updateFeedCache, updatePostInPaginatedPlaylistsCache } =
    useArticlesAndPlaylistsPaginationQuery({
      shouldFetch: false,
      filters: selectedFeedFilters,
    });
  const { updateUserArticlesCache } = useUserArticlesQuery({ shouldFetch: false });
  const { updatePaginatedRelatedArticlesCache } = usePaginatedRelatedArticlesQuery({
    articleId,
    shouldFetch: false,
  });
  const { updateBookmarksCache } = useBookmarksQuery({ shouldFetch: false });
  const { updateFormDetailsCache } = useFormDetailsQuery({
    shouldFetch: false,
  });
  const openReport = useReportModalStore((state) => state.openReport);
  const openShareModal = useShareModalStore((state) => state.openModal);
  const { user, companyId, isNovaAdmin, isNovaOrCompanyAdmin, company } = useUserData();
  const { updateAssignedFormsCache } = useAssignedFormsQuery({
    companyId: companyId || '',
    assignedTo: user?.username || '',
    createdBy: user?.username || '',
    status: 'NEW-INPROGRESS',
    formCategory: FORMS_CONFIG_CATEGORY_TYPES.REQUEST_A_POST_CARD,
  });
  const { updateAssignedFormsCache: updateAllFormsAssignToCurrentUserCache } =
    useAssignedFormsQuery({
      companyId: companyId || '',
      assignedTo: user?.username || '',
      status: 'NEW-INPROGRESS',
    });
  const canForwardPosts = useMemo(
    () =>
      article?.originIndicator !== 'PLAYLIST' &&
      ((user?.associatedCompanies &&
        user?.associatedCompanies?.some(
          (_user) => _user.active && _user.companyId !== user.companyId,
        )) ||
        isNovaAdmin),
    [user, isNovaAdmin],
  );
  // const canPublishPost = useMemo(
  //   () => !((isNovaAdmin || isBuildWiseInsightsUser) && company?.settings?.externalFlag),
  //   [company?.settings.externalFlag, isNovaAdmin, isBuildWiseInsightsUser],
  // );
  const [articleState, dispatch] = useReducer(useArticleReducer, {
    loading: false,
    openInsightsModal: false,
    openInteractionsModal: false,
    openAddPostsToPlaylistModal: false,
    openPlaylistModal: false,
    openEndorseConfirmationModal: false,
    openDeleteModal: false,
    openEndorseInteractionsModal: false,
    openPostConfirmationModal: false,
    openTransferDraftModal: false,
    openInviteModal: false,
    openRejectModal: false,
    openEditPost: false,
    openCopyPost: false,
    openCollaborators: false,
    openNotRelevantArticleModal: false,
    openPostRevisionModal: false,
    openForwardPostModal: false,
    openSelectWorkspacesModal: false,
    openMovePostConfirmationModal: false,
    openMoveToDraftsConfirmationModal: false,
    convertPostToQuestion: false,
    lightbox: {
      open: false,
      fileName: '',
    },
  });
  const [searchParams] = useSearchParams();
  const { state: routeState } = useLocation();
  const searchKeyword = useMemo(
    () => decodeURIComponent(searchParams?.get('q') || ''),
    [searchParams],
  );
  const isAlreadyEndorsed = useMemo(
    () => !!article.endorsements?.find((endorsement) => endorsement.userName === user?.username),
    [article.endorsements, user?.username],
  );
  const [isShortView, isDetailedView, isDraftView, isListView, isRelatedPostView] = useMemo(
    () => [
      variant === 'short',
      variant === 'detailed',
      variant === 'draft',
      variant === 'list',
      variant === 'related-post',
    ],
    [variant],
  );

  const { documents, mediaFiles } = useMemo(
    () =>
      splitFilesByType({
        files: article?.attachments || [],
        extraFileNameAttachedByService: getExtraFileNameAttachedByService<ArticleType>(article),
        coverPhotoFileName: article.coverPhotoFileName,
      }),

    [article.attachments, article.coverPhotoFileName],
  );
  const canViewInteractions = useMemo(
    () => isNovaAdmin || user?.username === article.createdBy,
    [isNovaAdmin, user?.username, article.createdBy],
  );
  const isPublishedByBuildWiseInsights = useMemo(
    () => article?.createdBy?.toLowerCase() === 'buildwise.insights@buildwise.ai',
    [article.createdBy],
  );
  const isAutoGeneratedDraft = useMemo(
    () => article.isDraft && article.autoDraftFlag,
    [article.isDraft, article.autoDraftFlag],
  );
  const [hasEditAccess, isPostOwner] = useMemo(
    () => [
      article.createdBy === user?.username ||
        article.taskOwner === user?.username ||
        isNovaOrCompanyAdmin ||
        Boolean(user && article.collaborators?.includes(user.username)),
      Boolean(user && article.createdBy === user.username),
    ],
    [article, user, isNovaOrCompanyAdmin],
  );
  const { collectionsModule, singularCollectionsModuleName } = useBuildWiseModules();

  const likeHandler = async () => {
    if (!user) return;
    try {
      const isLiked = !article.userLiked;
      const updatedArticle = {
        ...article,
        userLiked: isLiked,
        totalLikes: isLiked ? article.totalLikes + 1 : article.totalLikes - 1,
      };
      likeUpdateCallback?.({
        oldArticle: article,
        updatedArticle,
      });
      updateFeedCache({
        action: 'update',
        article: updatedArticle,
      });
      updateUserArticlesCache({
        action: 'update',
        article: updatedArticle,
        _username: updatedArticle.createdBy,
      });

      if (article?.isArticleBookMarked) {
        updateBookmarksCache({ action: 'update', article: updatedArticle });
      }

      if (stakeholderId) {
        updateStakeholderArticlesCache('update', stakeholderId, updatedArticle);
      }

      if (project?.id) {
        updateProjectRecommendationsQueryCache({
          companyId,
          projectId: project?.id,
          articles: [updatedArticle],
          action: 'update-article',
        });
      }
      updateArticleDetailsCache(companyId, updatedArticle);
      updateSearchQueriesCache<ArticleType>(companyId, searchKeyword, 'articles', updatedArticle);
      if (updatedArticle.isDraft) {
        updateDraftsCache(article.companyId, updatedArticle, 'update');
      }
      if (project) {
        const updatedArticles = [...project.articles];
        const articleIndex = updatedArticles.findIndex((item) => item.id === article.id);
        if (articleIndex > -1) {
          updatedArticles[articleIndex] = updatedArticle;
          updateProjectDetailsCache(companyId, {
            ...project,
            articles: updatedArticles,
          });
        }
      }

      updatePaginatedRelatedArticlesCache({ updateArticleId: article.id, updatedArticle });

      if (isLiked) {
        await KnowledgeAPI.like(user.companyId, article.id, user.username);
      } else {
        await KnowledgeAPI.removeLike(user.companyId, article.id, user.username);
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  const bookmarkHandler = async () => {
    if (!user) return;
    try {
      const isBookmarked = !article.isArticleBookMarked;
      const updatedArticle = { ...article, isArticleBookMarked: isBookmarked };
      bookmarkUpdateCallback?.({
        oldArticle: article,
        updatedArticle,
      });
      updateFeedCache({
        action: 'update',
        article: updatedArticle,
      });
      updateUserArticlesCache({
        action: 'update',
        article: updatedArticle,
        _username: updatedArticle.createdBy,
      });
      updateBookmarksCache({ action: isBookmarked ? 'add' : 'remove', article: updatedArticle });

      if (stakeholderId) {
        updateStakeholderArticlesCache('update', stakeholderId, updatedArticle);
      }

      if (project?.id) {
        updateProjectRecommendationsQueryCache({
          companyId,
          projectId: project?.id,
          articles: [updatedArticle],
          action: 'update-article',
        });
      }
      updateArticleDetailsCache(companyId, updatedArticle);
      updateSearchQueriesCache<ArticleType>(companyId, searchKeyword, 'articles', updatedArticle);
      if (project) {
        const updatedArticles = [...project.articles];
        const articleIndex = updatedArticles.findIndex((item) => item.id === article.id);
        if (articleIndex > -1) {
          updatedArticles[articleIndex] = updatedArticle;
          updateProjectDetailsCache(companyId, {
            ...project,
            articles: updatedArticles,
          });
        }
      }

      if (isBookmarked) {
        await KnowledgeAPI.addBookmark(user.companyId, article.id);
      } else {
        await KnowledgeAPI.removeBookmark(user.companyId, article.id);
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    }
  };

  const endorseHandler = async () => {
    try {
      dispatch({ type: 'TOGGLE_LOADING', payload: true, key: 'loading' });
      const payload = {
        ...article,
        endorsements: [
          ...(article?.endorsements || []),
          {
            userName: user?.username,
            userNameToDisplay: user?.userNameToDisplay,
            title: user?.title,
            expYears: user?.expYears,
          } as Endorsement,
        ],
      };
      const { data: updatedArticle } = await KnowledgeAPI.editArticle(
        companyId,
        article.id,
        payload,
      );
      toast.success('Feed Endorsed Successfully');
      dispatch({
        type: 'TOGGLE_ENDORSE_CONFIRMATION_MODAL',
        payload: false,
        key: 'openEndorseConfirmationModal',
      });
      endorseArticleCallback?.({
        oldArticle: article,
        updatedArticle,
      });
      updateArticleDetailsCache(companyId, updatedArticle);
      updateFeedCache({
        action: 'update',
        article: updatedArticle,
      });
      updateUserArticlesCache({
        action: 'update',
        article: updatedArticle,
        _username: updatedArticle.createdBy,
      });

      if (updatedArticle?.isArticleBookMarked) {
        updateBookmarksCache({ action: 'update', article: updatedArticle });
      }

      if (stakeholderId) {
        updateStakeholderArticlesCache('update', stakeholderId, updatedArticle);
      }

      if (project?.id) {
        updateProjectRecommendationsQueryCache({
          companyId,
          projectId: project?.id,
          articles: [updatedArticle],
          action: 'update-article',
        });
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      dispatch({ type: 'TOGGLE_LOADING', payload: false, key: 'loading' });
    }
  };

  const deleteArticle = async () => {
    try {
      dispatch({ type: 'TOGGLE_LOADING', payload: true, key: 'loading' });
      await KnowledgeAPI.removePost(companyId, article.id);
      dispatch({
        type: 'TOGGLE_DELETE_POST_MODAL',
        payload: false,
        key: 'openDeleteModal',
      });

      deleteArticleCallback?.({
        article,
      });
      if (article.isDraft) {
        toast.success('Draft deleted successfully');
        updateDraftsCache(companyId, article, 'delete');
        if (isDetailedView) {
          navigate(DRAFTS);
        }
        if (article?.formIds?.length) {
          // updating the forms cache to update the button status to create a post in request a post screen
          updateAssignedFormsCache({
            form: { id: article?.formIds[0], articleIds: [] },
            action: 'update',
          });
        }
      } else {
        toast.success(
          `${article.articleType === ArticleSourceType.QUESTION ? 'Question' : article.articleType === ArticleSourceType.SUGGESTION ? 'Suggestion' : article.articleType === ArticleSourceType.LESSON ? 'Learning' : article.articleType === ArticleSourceType.HELP_CENTER ? (article?.originIndicator === 'PLAYLIST' ? 'Collection' : 'Article') : 'Post'} deleted successfully`,
        );
        updateFeedCache({
          action: 'delete',
          article,
        });

        if (article?.isArticleBookMarked) {
          updateBookmarksCache({ action: 'remove', article });
        }

        if (stakeholderId) {
          updateStakeholderArticlesCache('delete', stakeholderId, article);
        }

        if (project?.id) {
          updateProjectRecommendationsQueryCache({
            companyId,
            projectId: project?.id,
            articles: [article],
            action: 'delete-article',
          });
        }
        if (isDetailedView) {
          if (article?.articleType === ArticleSourceType.HELP_CENTER) {
            navigate(HELP_CENTER);
          } else {
            navigate(HOME);
          }
        }
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      dispatch({ type: 'TOGGLE_LOADING', payload: false, key: 'loading' });
    }
  };

  const movePost = async () => {
    try {
      dispatch({ type: 'TOGGLE_LOADING', payload: true, key: 'loading' });
      await KnowledgeAPI.moveArticle(companyId, article.id, article);
      toast.success(
        `${article.articleType === ArticleSourceType.QUESTION ? 'Question' : article.articleType === ArticleSourceType.SUGGESTION ? 'Suggestion' : article.articleType === ArticleSourceType.LESSON ? 'Learning' : 'Post'} Moved To Top`,
      );
      dispatch({
        type: 'TOGGLE_MOVE_POST_MODAL',
        payload: false,
        key: 'openMovePostConfirmationModal',
      });

      if (isDetailedView) {
        navigate(HOME);
      } else {
        resetFeed?.({ resetQueries: true, smoothScrollBehavior: true });
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      dispatch({ type: 'TOGGLE_LOADING', payload: false, key: 'loading' });
    }
  };

  const moveToDrafts = async () => {
    try {
      dispatch({ type: 'TOGGLE_LOADING', payload: true, key: 'loading' });
      const articleResponse = await KnowledgeAPI.editArticle(companyId, article.id, {
        ...article,
        status: 'REVIEW_PENDING',
        isDraft: true,
      });
      toast.success(
        `The ${(article.articleType === ArticleSourceType.QUESTION ? 'Question' : article.articleType === ArticleSourceType.SUGGESTION ? 'Suggestion' : article.articleType === ArticleSourceType.LESSON ? 'Learning' : 'Post').toLowerCase()} is moved to your drafts`,
      );
      if (article?.formIds?.length) {
        updateFormDetailsCache({ article: articleResponse?.data, action: 'update' });
      }
      dispatch({
        type: 'TOGGLE_MOVE_TO_DRAFTS_MODAL',
        payload: false,
        key: 'openMoveToDraftsConfirmationModal',
      });
      updateFeedCache({
        action: 'delete',
        article,
      });
      updateDraftsCache(companyId, { ...article, status: 'REVIEW_PENDING', isDraft: true }, 'add');

      if (isDetailedView) {
        navigate(HOME);
      }
    } catch (e) {
      toast.error(catchErrorMessage(e));
    } finally {
      dispatch({ type: 'TOGGLE_LOADING', payload: false, key: 'loading' });
    }
  };

  const publishArticle = async () => {
    try {
      dispatch({ type: 'TOGGLE_LOADING', payload: true, key: 'loading' });
      const updatedArticle = {
        ...article,
        isDraft: false,
        status: 'PUBLISHED',
      };
      const { data: articleRes } = await KnowledgeAPI.editArticle(
        updatedArticle.companyId,
        updatedArticle.id,
        updatedArticle,
      );
      const articleTypeDisplayName =
        article.articleType === ArticleSourceType.QUESTION
          ? 'Question'
          : article.articleType === ArticleSourceType.SUGGESTION
            ? 'Suggestion'
            : article.articleType === ArticleSourceType.LESSON
              ? 'Learning'
              : 'Post';
      toast.success(
        article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON
          ? `${articleTypeDisplayName} submitted successfully!`
          : `${articleTypeDisplayName} published successfully!`,
      );
      if (article?.formIds?.length) {
        // updating the below cache to sync the data in requests and home screen
        updateAssignedFormsCache({
          form: { ...article, id: article?.formIds[0] },
          action: 'delete',
        });
        updateAllFormsAssignToCurrentUserCache({
          form: { ...article, id: article?.formIds[0] },
          action: 'delete',
        });
      }
      updateDraftsCache(articleRes.companyId, articleRes, 'delete');
      updateFeedCache({
        action: 'update',
        article: articleRes,
      });
      updatePostInPaginatedPlaylistsCache({ article: articleRes });
      if (isDetailedView) {
        navigate(HOME);
      }
      dispatch({
        type: 'TOGGLE_POST_CONFIRMATION_MODAL',
        payload: false,
        key: 'openPostConfirmationModal',
      });
    } catch (error) {
      console.error('PUBLISH_ARTICLE_ERROR', error);
      toast.error(catchErrorMessage(error));
    } finally {
      dispatch({ type: 'TOGGLE_LOADING', payload: false, key: 'loading' });
    }
  };

  const viewInteractionsHandler = () => {
    if (canViewInteractions) {
      dispatch({ type: 'TOGGLE_INTERACTIONS_MODAL', payload: true, key: 'openInteractionsModal' });
    }
  };

  const [menu, autoGeneratedDraftMenu] = useMemo(() => {
    const MENU: MenuItemType[] = [
      // {
      //   title:
      //     article.articleType === ArticleSourceType.QUESTION ||
      //     article.articleType === ArticleSourceType.SUGGESTION
      //       ? 'Submit'
      //       : 'Post',
      //   icon:
      //     article.articleType === ArticleSourceType.QUESTION ? (
      //       <QuestionLineIcon size={17} />
      //     ) : article.articleType === ArticleSourceType.SUGGESTION ? (
      //       <ChatQuoteLineIcon size={17} />
      //     ) : (
      //       <FileListLineIcon size={17} />
      //     ),
      //   onClick: () =>
      //     dispatch({
      //       type: 'TOGGLE_POST_CONFIRMATION_MODAL',
      //       payload: true,
      //       key: 'openPostConfirmationModal',
      //     }),
      //   isHidden: !article.isDraft || !canPublishPost,
      // },
      {
        title: 'Edit',
        icon: <EditLineIcon size={17} />,
        onClick: () => {
          if (onEditArticleCallback) {
            onEditArticleCallback();
          } else {
            dispatch({ type: 'TOGGLE_EDIT_MODAL', payload: true, key: 'openEditPost' });
          }
        },
        isHidden: !hasEditAccess,
      },
      {
        title: 'Convert to Question',
        icon: (
          <QuestionLineIcon
            size={20}
            color={theme.palette.custom.link}
            style={{ marginLeft: '-2px' }}
          />
        ),
        onClick: () => {
          if (onEditArticleCallback) {
            onEditArticleCallback();
          } else {
            dispatch({ type: 'TOGGLE_EDIT_MODAL', payload: true, key: 'openEditPost' });
            dispatch({ type: 'TOGGLE_CONVERT_POST_TO_QUESTION', payload: true });
          }
        },
        isHidden: !(
          (isNovaOrCompanyAdmin || article.createdBy === user?.username) &&
          article?.articleType === ArticleSourceType.POST
        ),
      },
      {
        title: 'Make a Copy',
        icon: <FileCopyLineIcon size={17} />,
        onClick: () => dispatch({ type: 'TOGGLE_COPY_POST', payload: true, key: 'openCopyPost' }),
        isHidden:
          !isNovaAdmin ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Assign',
        subTitle: 'Transfer ownership of this draft to another member.',
        icon: <UserShared2LineIcon size={17} />,
        onClick: () =>
          dispatch({
            type: 'TOGGLE_TRANSFER_DRAFT_MODAL',
            payload: true,
            key: 'openTransferDraftModal',
          }),
        isHidden:
          !article.isDraft ||
          article.createdBy !== user?.username ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
        relatedTo: 'auto-generated-drafts',
      },
      {
        title: 'Invite',
        subTitle: 'Invite others to collaborate on this draft.',
        icon: <UserAddLineIcon size={17} />,
        onClick: () =>
          dispatch({ type: 'TOGGLE_INVITE_MODAL', payload: true, key: 'openInviteModal' }),
        isHidden:
          !article.isDraft ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
        relatedTo: 'auto-generated-drafts',
      },
      {
        title: 'Endorse',
        icon: <StarSmileLineIcon size={17} />,
        onClick: () => {
          dispatch({
            type: 'TOGGLE_ENDORSE_CONFIRMATION_MODAL',
            payload: true,
            key: 'openEndorseConfirmationModal',
          });
        },
        isHidden:
          article.isDraft ||
          !user?.canEndorseFlag ||
          isAlreadyEndorsed ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: article.isArticleBookMarked ? 'Remove Bookmark' : 'Bookmark',
        icon: (
          <BookmarkIcon
            style={{ marginLeft: -1, color: theme.palette.custom.body, width: 18, height: 18 }}
          />
        ),
        onClick: bookmarkHandler,
        isHidden: article.isDraft || article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: `Add to ${singularCollectionsModuleName}`,
        icon: <PlayListAddLineIcon size={17} />,
        onClick: () =>
          dispatch({
            type: 'TOGGLE_PLAYLIST_MODAL',
            payload: true,
            key: 'openPlaylistModal',
          }),
        isHidden:
          article.isDraft ||
          !collectionsModule?.isActive ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER ||
          article.originIndicator === 'PLAYLIST',
      },
      {
        title: 'Move to Top',
        icon: <ArrowUpLineIcon size={17} />,
        onClick: () =>
          dispatch({
            type: 'TOGGLE_MOVE_POST_MODAL',
            payload: true,
            key: 'openMovePostConfirmationModal',
          }),
        isHidden:
          article.isDraft ||
          !user?.canMovePostsToTopFlag ||
          articleIndex === 0 ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Move to Drafts',
        icon: <DraftLineIcon size={17} />,
        onClick: () =>
          dispatch({
            type: 'TOGGLE_MOVE_TO_DRAFTS_MODAL',
            payload: true,
            key: 'openMoveToDraftsConfirmationModal',
          }),
        isHidden:
          article.isDraft ||
          !isPostOwner ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Suggest Revision',
        icon: <UserShared2LineIcon size={17} />,
        onClick: () =>
          dispatch({
            type: 'TOGGLE_POST_REVISION_MODAL',
            payload: true,
            key: 'openPostRevisionModal',
          }),
        isHidden:
          article.isDraft ||
          !user?.canSuggestRevisiontToPostsFlag ||
          isPostOwner ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Share',
        icon: <ShareLineIcon size={17} />,
        onClick: () => openShareModal({ shareData: article }),
        isHidden: article.isDraft || article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Forward to Workspace',
        icon: <ShareForwardLineIcon size={17} />,
        onClick: () =>
          dispatch({
            type: 'TOGGLE_SELECT_WORKSPACES_MODAL',
            payload: true,
            key: 'openSelectWorkspacesModal',
          }),
        isHidden:
          !canForwardPosts ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Report',
        icon: <FlagLineIcon size={17} />,
        onClick: () =>
          openReport({ id: article.id, text: article.title, reportObject: 'Article', article }),
        isHidden:
          article.isDraft ||
          article.createdBy === user?.username ||
          article.articleType === ArticleSourceType.HELP_CENTER,
      },
      {
        title: 'Delete',
        titleColor: theme.palette.custom.redWine,
        icon: <DeleteBinLineIcon size={17} color={theme.palette.custom.redWine} />,
        onClick: () =>
          dispatch({ type: 'TOGGLE_DELETE_POST_MODAL', payload: true, key: 'openDeleteModal' }),
        isHidden: (!isPostOwner || isAutoGeneratedDraft) && !isNovaAdmin,
      },
      {
        title: 'Reject',
        subTitle: 'Provide feedback to improve auto-generated drafts.',
        icon: <CloseCircleLineIcon size={17} color={theme.palette.custom.redWine} />,
        onClick: () =>
          dispatch({ type: 'TOGGLE_REJECT_MODAL', payload: true, key: 'openRejectModal' }),
        isHidden:
          !article.isDraft ||
          !isAutoGeneratedDraft ||
          article.articleType === ArticleSourceType.QUESTION ||
          article.articleType === ArticleSourceType.SUGGESTION ||
          article.articleType === ArticleSourceType.LESSON ||
          article.articleType === ArticleSourceType.HELP_CENTER,
        relatedTo: 'auto-generated-drafts',
      },
    ];

    return [
      MENU.filter((item) => !item.isHidden),
      MENU.filter((item) => !item.isHidden && item.relatedTo === 'auto-generated-drafts'),
    ];
  }, [
    article,
    hasEditAccess,
    user?.username,
    user?.canEndorseFlag,
    isAlreadyEndorsed,
    user?.canSuggestRevisiontToPostsFlag,
    isAutoGeneratedDraft,
    isPostOwner,
    isNovaAdmin,
    canForwardPosts,
    company?.settings,
    singularCollectionsModuleName,
    collectionsModule,
  ]);

  const openInsightsBanner = () => {
    if (isPublishedByBuildWiseInsights) {
      dispatch({
        type: 'TOGGLE_INSIGHTS_MODAL',
        payload: true,
        key: 'openInsightsModal',
      });
    }
  };

  return {
    articleState,
    autoGeneratedDraftMenu,
    documents,
    isPostOwner,
    hasEditAccess,
    isAutoGeneratedDraft,
    isDetailedView,
    isDraftView,
    isListView,
    isPublishedByBuildWiseInsights,
    isRelatedPostView,
    isShortView,
    mediaFiles,
    menu,
    pathname,
    routeState,
    searchKeyword,
    selectedCompanyIds,
    bookmarkHandler,
    deleteArticle,
    movePost,
    moveToDrafts,
    dispatch,
    endorseHandler,
    likeHandler,
    openInsightsBanner,
    publishArticle,
    setSelectedCompanyIds,
    viewInteractionsHandler,
  };
}

export default useArticle;
